import { z } from 'zod';
import { SIGN_IN_METHODS } from '../enums.js';
import { uuid } from './common.js';
/** Dev-only "Dev login as…" (FR-AUTH-8; `AUTH_DEV_LOGIN=true` and not production). */
export const devLoginSchema = z.object({ userId: uuid });
/**
 * Why a sign-in was refused (FR-AUTH-2..5, FR-AUTH-9…12). Google refusals redirect to
 * `/login?error=<code>`; password sign-in answers problem+json with the code in `code`. The login
 * page shows the matching message. Codes never reveal more than the user already knows: an unknown
 * email and a wrong password are both `invalid_credentials` (FR-AUTH-19).
 */
export const AUTH_ERROR_CODES = [
    'email_unverified',
    'not_registered',
    'account_disabled',
    'exited',
    'sub_mismatch',
    'google_unavailable',
    'sign_in_failed',
    // Password sign-in (v1.3, ADR-091)
    'invalid_credentials',
    'captcha_required',
    'captcha_failed',
    'locked',
    'use_google',
    'use_password',
    'password_not_set',
];
export const AUTH_ERROR_MESSAGES = {
    email_unverified: "Your Google account email isn't verified. Verify it with Google and try again.",
    not_registered: "Your Google account isn't registered in ThriveHRM. Contact HR.",
    account_disabled: 'Your ThriveHRM account is disabled. Contact HR.',
    exited: 'Your access ended after your last working day. Contact HR if this is wrong.',
    sub_mismatch: 'This email is linked to a different Google account. Contact an administrator to reset it.',
    google_unavailable: "Google sign-in isn't available right now. Try again later.",
    sign_in_failed: "Sign-in didn't complete. Please try again.",
    invalid_credentials: 'The email or password is incorrect.',
    captcha_required: "Confirm you're not a robot, then sign in again.",
    captcha_failed: 'The "I\'m not a robot" check didn\'t pass. Try it again.',
    locked: 'Too many failed sign-ins. Try again in 15 minutes, or reset your password with "Forgot password?".',
    use_google: 'This account signs in with Google. Use "Continue with Google".',
    use_password: 'This email signs in with a password, not Google. Use the email and password form below.',
    password_not_set: 'You have not set a password yet. Use the link in your invitation email, or "Forgot password?" to get a new one.',
};
export function isAuthErrorCode(value) {
    return typeof value === 'string' && AUTH_ERROR_CODES.includes(value);
}
/**
 * True when the email's domain is one of the Google Workspace domains (FR-AUTH-9, ADR-091):
 * exact domain, case-insensitive, no sub-domains. Such accounts sign in with Google; every other
 * login email signs in with a password.
 */
export function isWorkspaceEmail(email, domains) {
    const at = email.lastIndexOf('@');
    if (at < 1 || at === email.length - 1)
        return false;
    const domain = email
        .slice(at + 1)
        .trim()
        .toLowerCase();
    return domains.some((d) => d.trim().toLowerCase() === domain);
}
/** How a login email signs in (FR-AUTH-9): Google for Workspace domains, a password otherwise. */
export function signInMethodOf(email, domains) {
    return isWorkspaceEmail(email, domains) ? 'GOOGLE' : 'PASSWORD';
}
/** `GET /auth/config` (public, FR-MAIL-6): what the login and forgot-password pages need. */
export const authConfigResponseSchema = z.object({
    /** "Continue with Google" is configured on the server. */
    googleEnabled: z.boolean(),
    /** reCAPTCHA v2 site key; `null` while reCAPTCHA is not configured (FR-AUTH-12). */
    recaptchaSiteKey: z.string().nullable(),
});
/** `GET /auth/me` — the signed-in user (ARCHITECTURE §4 step 5, SRS §7). */
export const meResponseSchema = z.object({
    userId: uuid,
    name: z.string(),
    email: z.string(),
    /** Role codes held (`EMPLOYEE`, `HR`, `ADMIN`, or roles added by later modules). */
    roles: z.array(z.string()),
    /** Effective permissions — the UI only hides what the user cannot use; the API decides. */
    permissions: z.array(z.string()),
    /** Has at least one current direct report (grants `team.read`). */
    hasReports: z.boolean(),
    employee: z.object({ id: uuid, empCode: z.number().int(), fullName: z.string() }).nullable(),
    /** How this account signs in, from its login email's domain (FR-AUTH-9, ADR-091). */
    signInMethod: z.enum(SIGN_IN_METHODS),
});
/** `GET /auth/dev/accounts` — accounts offered by "Dev login as…" (FR-AUTH-8). */
export const devLoginAccountsResponseSchema = z.object({
    items: z.array(z.object({
        userId: uuid,
        name: z.string(),
        email: z.string(),
        empCode: z.number().int(),
        roles: z.array(z.string()),
    })),
});
//# sourceMappingURL=auth.js.map